// Cullector SEO copy templates — single source of truth for the hub, category
// and article routes, consumed by both the client (src/pages/cullector.tsx,
// via Babel's ES module interop) and the plain-Node custom server
// (server/pages.js, via require()) so bots and browsers always render
// identical <title>/<meta description>/heading/keyword copy for the same URL.
// The Home page's equivalent copy lives in src/locales/en/en.json instead —
// already a single i18n source shared the same way.

const CULLECTOR_HOME_TITLE =
  'Product Reviews, Gift Guides & Buying Guides | Cullector'
const CULLECTOR_HOME_DESCRIPTION =
  'Find the best products and top picks for home, style, tech and more. Explore gift ideas, product comparisons and shopping recommendations for every budget.'
const CULLECTOR_HOME_H1 = "Discover What's Worth Buying"
const CULLECTOR_HOME_H2 = 'Explore Top Picks for Home, Style, Tech & More'
const CULLECTOR_HOME_KEYWORDS = [
  'best products',
  'top picks',
  'gift ideas',
  'gift guides',
  'gifts for every occasion',
  'what to buy',
  'product comparisons',
  'home',
  'style',
  'tech',
]

const CULLECTOR_SEARCH_TITLE = 'Search Guides | Cullector'

// Google/Bing truncate past these lengths anyway, but a category name long
// enough to push the templated copy over the limit (e.g. "Health & Fitness")
// should still produce a clean, complete-reading tag rather than an
// abruptly-cut one — trim at the last word boundary, same tolerance the
// sheet itself specifies for article descriptions ("trim ... preserving
// meaning").
const TITLE_MAX_LENGTH = 70
const DESCRIPTION_MAX_LENGTH = 160

const truncateAtWordBoundary = (str, maxLength) => {
  if (str.length <= maxLength) return str
  const truncated = str.slice(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  const safe = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated
  return safe.replace(/[,;:]+$/, '')
}

// Fallback copy for pages without their own <SEO> (defaultLayout), and the /shop page's copy.
const DEFAULT_PAGE_SEO = {
  title: 'Shop and Earn cash back',
  description:
    'Shop 1000+ stores. Share style finds. Make money when you or friends buy from your personal boutique. Fashion, home, beauty, travel, lifestyle.',
}

// Editorial category names are stored as typed in admin, often lowercase ("home & garden").
const SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'for',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
])

const titleCase = str =>
  str
    .split(' ')
    .map((word, i) =>
      i > 0 && SMALL_WORDS.has(word.toLowerCase())
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ')

const cullectorCategoryTitle = categoryName =>
  truncateAtWordBoundary(
    `${titleCase(categoryName)} Reviews, Gift Guides & Buying Guides`,
    TITLE_MAX_LENGTH
  )

const cullectorCategoryDescription = categoryName => {
  const c = categoryName.toLowerCase()
  const base = `Explore the best ${c} products, top picks and gift ideas. Compare options for different needs and budgets`
  const full = `${base}, from everyday essentials to special finds.`
  if (full.length <= DESCRIPTION_MAX_LENGTH) return full

  // A long category name (e.g. "Tech & Entertainment") can push the trailing
  // clause over the limit — drop it for a shorter, still-complete sentence
  // rather than a word-boundary cut that reads mid-thought.
  const short = `${base}.`
  return short.length <= DESCRIPTION_MAX_LENGTH
    ? short
    : truncateAtWordBoundary(short, DESCRIPTION_MAX_LENGTH)
}

const cullectorCategoryH1 = categoryName =>
  `${titleCase(categoryName)} Finds for Every Need and Budget`

const cullectorCategoryH2 = categoryName =>
  `Top Picks, Comparisons & Gift Ideas in ${titleCase(categoryName)}`

const cullectorCategoryKeywords = categoryName => {
  const c = categoryName.toLowerCase()
  return [
    `best ${c} products`,
    `top ${c} picks`,
    `${c} gift ideas`,
    `${c} gift guides`,
    `${c} recommendations`,
    `${c} comparisons`,
  ]
}

// Category listings page through their articles with ?page=N (N > 1); page 1 is the bare path.
const CULLECTOR_CATEGORY_PAGE_SIZE = 24

const pagedPath = (path, page) => (page > 1 ? `${path}?page=${page}` : path)

// Breadcrumb trail for Cullector pages, shared by the visible crawler breadcrumb (server/pages.js)
// and the BreadcrumbList JSON-LD (both sides). `category` may itself be a subcategory carrying its
// `parent`, as the category landing API returns it. The last item is the current page.
const cullectorBreadcrumbs = ({ category, subcategory, title } = {}) => {
  const items = [
    { name: 'Chirpyest', path: '/' },
    { name: 'Cullector', path: '/cullector/' },
  ]
  const parent = category && category.parent
  if (parent) {
    items.push({
      name: titleCase(parent.name),
      path: `/cullector/${parent.slug}`,
    })
  }
  if (category) {
    items.push({
      name: titleCase(category.name),
      path: parent
        ? `/cullector/${parent.slug}/${category.slug}`
        : `/cullector/${category.slug}`,
    })
  }
  if (category && subcategory) {
    items.push({
      name: titleCase(subcategory.name),
      path: `/cullector/${category.slug}/${subcategory.slug}`,
    })
  }
  if (title) items.push({ name: title })
  return items
}

// `url` is the current page's canonical URL, used for the item that carries no path.
const breadcrumbListJsonLd = (items, siteUrl, url) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.path ? `${siteUrl}${item.path}` : url,
  })),
})

// Hub and category structured data. `siteUrl` is the canonical origin (GATSBY_SITE_URL); URLs
// match the canonical tags, so the hub is `/cullector/` with its slash.
const cullectorHubUrl = siteUrl => `${siteUrl}/cullector/`

const cullectorHomeJsonLd = (siteUrl, categoryNames = []) => {
  const hubUrl = cullectorHubUrl(siteUrl)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${hubUrl}#organization`,
        name: 'Cullector',
        url: hubUrl,
        slogan: 'The best, curated.',
        parentOrganization: {
          '@type': 'Organization',
          name: 'Chirpyest',
          url: `${siteUrl}/`,
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${hubUrl}#website`,
        url: hubUrl,
        name: 'Cullector',
        description: 'Product reviews and buying guides, tested and compared.',
        publisher: { '@id': `${hubUrl}#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/cullector/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        ...breadcrumbListJsonLd(cullectorBreadcrumbs(), siteUrl),
        '@context': undefined,
      },
      {
        '@type': 'CollectionPage',
        '@id': `${hubUrl}#collection`,
        url: hubUrl,
        name: 'Cullector Buying Guides',
        isPartOf: { '@id': `${hubUrl}#website` },
        ...(categoryNames.length > 0 ? { about: categoryNames } : {}),
      },
    ],
  }
}

// `category` as the category landing API returns it (a subcategory carries its `parent`).
const cullectorCategoryJsonLd = (siteUrl, category) => {
  const crumbs = cullectorBreadcrumbs({ category })
  const url = `${siteUrl}${crumbs[crumbs.length - 1].path}`
  return {
    '@context': 'https://schema.org',
    '@graph': [
      { ...breadcrumbListJsonLd(crumbs, siteUrl, url), '@context': undefined },
      {
        '@type': 'CollectionPage',
        url,
        name: `${titleCase(category.name)} Guides`,
        isPartOf: { '@id': `${cullectorHubUrl(siteUrl)}#website` },
      },
    ],
  }
}

// Brand and product page copy. `translate(key)` returns the interpolated `seo.brandPage.<key>` /
// `seo.product.<key>` string from src/locales/en/en.json: react-i18next's `t` in the browser, a
// plain lookup in server/pages.js, so both render the same tags for the same URL.
const BRAND_META_FORBIDDEN_TERMS = /cash\s*back|discount|commission|rebate/i

const brandSeoCopy = (brand, translate) => {
  const showCashback = brand.isCashbackShownInWebsite !== false
  // The AI-generated `seoMetaDescription` is only trusted when it is safe for this brand's flag:
  // the regeneration cron may not have caught up after cash back was hidden.
  const persistedMeta = brand.seoMetaDescription || ''
  const persistedMetaSafe =
    showCashback || !BRAND_META_FORBIDDEN_TERMS.test(persistedMeta)
  return {
    title: translate(showCashback ? 'title' : 'titleNoCashback'),
    description:
      (persistedMetaSafe && persistedMeta) ||
      translate(showCashback ? 'description' : 'descriptionNoCashback'),
    keywords: translate(showCashback ? 'keywords' : 'keywordsNoCashback'),
  }
}

// The product templates use bracket placeholders rather than i18next interpolation.
const fillProductTemplate = (template, product) =>
  template
    .replace('[Brand]', product.brand_name)
    .replace('[max %]', product.cashBack)
    .replace('[Product name]', product.name)

const productSeoCopy = (product, translate) => ({
  title: fillProductTemplate(translate('title'), product),
  description: fillProductTemplate(translate('description'), product),
})

// Shop category pages (/category/<name>, not Cullector). A `_` in the URL segment marks a
// subcategory (e.g. `fashion_clothing`). `translate(key, name)` returns the interpolated
// `seo.category.<key>` string.
const shopCategorySeoCopy = (category, urlSegment, translate) => {
  const name = category.name.charAt(0).toUpperCase() + category.name.slice(1)
  const isSub = urlSegment.includes('_')
  return {
    title: translate(isSub ? 'titleSub' : 'title', name),
    description: translate(isSub ? 'descriptionSub' : 'description', name),
    keywords: translate('keywords', name),
  }
}

module.exports = {
  DEFAULT_PAGE_SEO,
  CULLECTOR_CATEGORY_PAGE_SIZE,
  pagedPath,
  CULLECTOR_HOME_TITLE,
  CULLECTOR_HOME_DESCRIPTION,
  CULLECTOR_HOME_H1,
  CULLECTOR_HOME_H2,
  CULLECTOR_HOME_KEYWORDS,
  CULLECTOR_SEARCH_TITLE,
  cullectorCategoryTitle,
  cullectorCategoryDescription,
  cullectorCategoryH1,
  cullectorCategoryH2,
  cullectorCategoryKeywords,
  titleCase,
  cullectorBreadcrumbs,
  breadcrumbListJsonLd,
  cullectorHomeJsonLd,
  cullectorCategoryJsonLd,
  brandSeoCopy,
  productSeoCopy,
  shopCategorySeoCopy,
}
